import React from 'react'
import { ChevronDown, Snail, Volume2 } from 'lucide-react'
import { Slider } from '../../components/ui/slider'
import { PodcastEditorProvider } from './context/podcast-editor-provider'
import { TopBar } from './components/topbar/top-bar'
import { VoiceSelectorForPodcast, LanguageSelector } from './components/sidebar'
import PublicToggle from '../../components/public-toggle'
import Tooltip from '../../components/ui/tooltip'
import { PodcastLinesEditor } from './components/editor/podcast-lines-editor'
import { usePodcastEditor } from './context'
import Dialog from '../../components/ui/dialog'
import { ScriptRefineChat } from './components/editor/script-refine-chat'
import { ExportSection } from './components/topbar/export-button'

export const EMOTIONS = [
  { value: 'neutral', label: '😐 Neutral' },
  { value: 'positivity', label: '😃 Positive' },
  { value: 'curiosity', label: '🤔 Curious' },
  { value: 'surprise', label: '😲 Surprised' },
  { value: 'sadness', label: '😢 Sad' },
  { value: 'anger', label: '😠 Angry' },
]

const AudioSettingsControls: React.FC = () => {
  const { speed, setSpeed, volume, setVolume } = usePodcastEditor()
  // Ensure defaults if undefined (though store should handle this)
  // emotion default is 'neutral'

  return (
    <div className="grid gap-6">
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Speed</label>
          <Slider
            value={speed || 1.0}
            min={0.5}
            max={2.0}
            step={0.1}
            onChange={setSpeed}
            leftLabel="Slow"
            rightLabel="Fast"
            icon={<Snail className="h-4 w-4" />}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Volume</label>
          <Slider
            value={volume || 1.0}
            min={0.1}
            max={2.0}
            step={0.1}
            onChange={setVolume}
            leftLabel="Quiet"
            rightLabel="Loud"
            icon={<Volume2 className="h-4 w-4" />}
          />
        </div>

      </div>
    </div>
  )
}

const GeneratedAudioSettingWarning = () => {
  const { podcast } = usePodcastEditor()
  const hasAnyGeneratedAudio = React.useMemo(() => {
    const lines = podcast?.podcastLines || []
    return lines.some(l => !!l.audioFileUrl && l.audioFileUrl.trim().length > 0)
  }, [podcast])

  if (!hasAnyGeneratedAudio) return null

  return <p className="text-xs text-foreground/60">
    Changing host voices will delete existing audio and require regeneration using additional credits.  </p>
}

const PodcastSettingsPanelContent: React.FC = () => {
  const { podcast, isGeneratingAudio, isGeneratingScript } = usePodcastEditor()
  const handleChange = async (next: boolean) => {
    if (!podcast) return
    try {
      const { updatePodcast } = await import('../../api')
      await updatePodcast(podcast.id, { isPublic: next })
    } finally {
    }
  }
  return (
    <div className="h-full flex flex-col">
      <div className="space-y-5">
        <VoiceSelectorForPodcast />
        <div className="grid grid-cols-1 gap-3">
          <LanguageSelector />
          <div className="h-px bg-black/10" />
          <div className="space-y-4 pt-2">
            <AudioSettingsControls />
          </div>
          <div className="h-px bg-black/10" />
          {(() => {
            const disabled = !!(isGeneratingAudio || isGeneratingScript)
            const toggle = (
              <PublicToggle value={!!podcast?.isPublic} onChange={handleChange} disabled={disabled} />
            )
            return disabled ? (
              <Tooltip content="Cannot change visibility while script or audio is being generated." side="top" align="start">
                <div className="w-full">{toggle}</div>
              </Tooltip>
            ) : toggle
          })()}
        </div>
      </div>
      <div className="flex-1" />
      <GeneratedAudioSettingWarning />
    </div>
  )
}

const PodcastEditorPageInner: React.FC = () => {
  const { podcast } = usePodcastEditor()
  const hasAnyGeneratedAudio = React.useMemo(() => {
    const lines = podcast?.podcastLines || []
    return lines.some(l => !!l.audioFileUrl && l.audioFileUrl.trim().length > 0)
  }, [podcast])

  const [settingsOpen, setSettingsOpen] = React.useState(false)

  // Resizable chat pane state
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const chatContentMeasureRef = React.useRef<HTMLDivElement | null>(null)
  const [chatHeight, setChatHeight] = React.useState<number | null>(null)
  const [isDragging, setIsDragging] = React.useState(false)
  const [collapsed, setCollapsed] = React.useState(false)
  const [measuredMin, setMeasuredMin] = React.useState<number | null>(null)

  // Resizing constraints and defaults
  const MIN_CHAT_HEIGHT = 120
  const DEFAULT_CHAT_HEIGHT = 110
  const COLLAPSE_THRESHOLD = 90

  // Measure default chat height from content once mounted
  React.useEffect(() => {
    if (chatHeight !== null) return
    const el = chatContentMeasureRef.current
    if (el) {
      // Prefer a compact default; do not exceed 240 on load
      const needed = Math.max(MIN_CHAT_HEIGHT, measuredMin ?? DEFAULT_CHAT_HEIGHT)
      const initial = Math.min(Math.max(needed, MIN_CHAT_HEIGHT), 300)
      setChatHeight(initial)
    }
  }, [chatHeight, measuredMin])

  // Collapse chat by default on mobile (when the sticky footer shows the icon-only chat button),
  // keep it open by default on desktop. Also react to viewport changes.
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 640px)') // Tailwind sm breakpoint
    const apply = () => {
      const isDesktop = mq.matches
      setCollapsed(!isDesktop)
    }
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  // Drag handlers for resize
  const onDragStart = React.useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return
    e.preventDefault()
    setIsDragging(true)
    const startY = e.clientY
    const startHeight = chatHeight ?? 220
    const container = containerRef.current
    // Improve UX during drag
    const prevUserSelect = document.body.style.userSelect
    const prevCursor = document.body.style.cursor
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'row-resize'
    const onMove = (ev: MouseEvent) => {
      const dy = ev.clientY - startY
      // dragging handle (above chat) down reduces chat height
      const next = startHeight - dy
      const maxH = Math.floor(container.getBoundingClientRect().height * 0.8)
      const clamped = Math.max(0, Math.min(maxH, next))
      const requiredMin = Math.max(MIN_CHAT_HEIGHT, measuredMin ?? 0)
      const shouldCollapse = clamped <= Math.max(COLLAPSE_THRESHOLD, requiredMin)
      setCollapsed(shouldCollapse)
      if (shouldCollapse) {
        setChatHeight(clamped)
      } else {
        setChatHeight(Math.max(requiredMin, clamped))
      }
    }
    const onUp = () => {
      setIsDragging(false)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.userSelect = prevUserSelect
      document.body.style.cursor = prevCursor
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [chatHeight])

  // Keyboard-accessible resizing for the handle
  const adjustChatHeight = React.useCallback((delta: number) => {
    if (!containerRef.current) return
    const container = containerRef.current
    const maxH = Math.floor(container.getBoundingClientRect().height * 0.8)
    const requiredMin = Math.max(MIN_CHAT_HEIGHT, measuredMin ?? 0)
    const current = chatHeight ?? requiredMin
    const next = current + delta
    const clamped = Math.max(0, Math.min(maxH, next))
    const shouldCollapse = clamped <= Math.max(COLLAPSE_THRESHOLD, requiredMin)
    setCollapsed(shouldCollapse)
    if (shouldCollapse) {
      setChatHeight(clamped)
    } else {
      setChatHeight(Math.max(requiredMin, clamped))
    }
  }, [chatHeight, measuredMin])

  // If dynamic content grows such that the current height can't contain it, collapse
  React.useEffect(() => {
    if (collapsed) return
    if (measuredMin === null) return
    const current = chatHeight ?? 0
    if (current >= measuredMin - 1) return
    const container = containerRef.current
    const maxH = container ? Math.floor(container.getBoundingClientRect().height * 0.85) : measuredMin
    const next = Math.min(Math.max(measuredMin, MIN_CHAT_HEIGHT), maxH)
    setChatHeight(next)
  }, [collapsed, chatHeight, measuredMin])

  const handleOpenChat = React.useCallback(() => {
    const el = chatContentMeasureRef.current
    const baseDefault = el ? Math.min(Math.max(DEFAULT_CHAT_HEIGHT, MIN_CHAT_HEIGHT), 240) : DEFAULT_CHAT_HEIGHT
    const desired = measuredMin !== null ? Math.max(baseDefault, measuredMin) : baseDefault
    const container = containerRef.current
    const maxH = container ? Math.floor(container.getBoundingClientRect().height * 0.85) : desired
    setChatHeight(Math.min(desired, maxH))
    setCollapsed(false)
  }, [measuredMin])

  // Collapse chat automatically when script-refine suggestions are ready/applied
  React.useEffect(() => {
    const handler = () => setCollapsed(true)
    if (typeof window !== 'undefined') {
      window.addEventListener('podcast:refine-suggestions-ready', handler as any)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('podcast:refine-suggestions-ready', handler as any)
      }
    }
  }, [])

  return (
    <div className="p-4">
      <TopBar />
      <div className="grid grid-cols-1 lg:grid-cols-[25%_1fr] gap-4 h-[calc(100vh-6rem)]">
        {/* Desktop aside */}
        <aside className="min-h-0 h-full overflow-auto space-y-5 rounded-xl border border-black/10 bg-gradient-to-b from-white to-black/[0.02] p-5 hidden lg:block">
          <PodcastSettingsPanelContent />
        </aside>
        {/* Main editor area with resizable chat below */}
        <section ref={containerRef} className="rounded-xl border border-black/10 bg-white h-full overflow-hidden relative flex flex-col">
          {/* Top: Editor (fills remaining space, scrolls as needed) */}
          <div className="min-h-0 overflow-auto flex flex-col" style={{ height: collapsed ? '100%' : undefined, flex: collapsed ? '1 1 auto' as any : '1 1 auto' }}>
            {/* Pass settings and chat controls to editor */}
            <PodcastLinesEditor
              onOpenSettings={() => setSettingsOpen(true)}
              settingsOpen={settingsOpen}
              onOpenChat={handleOpenChat}
              chatOpen={!collapsed}
            />
          </div>

          {/* Drag handle (hidden on mobile) */}
          {!collapsed && (
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize chat (drag or use arrow keys)"
              tabIndex={0}
              onKeyDown={(e) => {
                // ArrowUp increases editor area -> decreases chat height
                if (e.key === 'ArrowUp') { e.preventDefault(); adjustChatHeight(-24) }
                if (e.key === 'ArrowDown') { e.preventDefault(); adjustChatHeight(24) }
                if (e.key === 'Home') { e.preventDefault(); handleOpenChat() }
                if (e.key === 'End' || e.key === 'Enter') { e.preventDefault(); setCollapsed(true) }
              }}
              onMouseDown={onDragStart}
              onDoubleClick={() => setCollapsed(true)}
              title="Drag to resize • ↑/↓ to adjust • Enter to collapse"
              className={`group h-10 sm:h-12 cursor-row-resize border-t border-black/10 bg-gradient-to-b from-transparent to-black/[0.02] relative focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 ${isDragging ? 'bg-black/[0.04]' : ''} hidden sm:block`}
            >
              {/* Larger invisible hit zone with clear center grip */}
              <div className="absolute inset-0" />
              <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <span className="h-2 w-24 rounded-full bg-black/20 group-hover:bg-black/30 transition-colors" />
                <span className="sr-only">Resize</span>
              </div>
            </div>
          )}

          {/* Bottom: Chat (resizable, can collapse) */}
          {collapsed ? (
            <>
              {/* Desktop open bar */}
              <button
                onClick={handleOpenChat}
                className="hidden sm:flex flex-none h-8 text-[13px] text-foreground/60 hover:text-foreground/80 bg-white/80 backdrop-blur border-t border-black/10 items-center justify-center gap-2 cursor-pointer"
              >
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-secondary-100 text-secondary-700 ring-1 ring-inset ring-secondary-200 text-[10px]">💬</span>
                  <span className="font-medium">Open chat</span>
                </span>
              </button>
            </>
          ) : (
            <div className="flex-none border-t border-black/10 sm:mb-0 mb-[65px] bg-gradient-to-b from-white to-black/[0.02] transition-[height] duration-200 ease-out relative" style={{ height: chatHeight ?? undefined }}>
              {/* Mobile collapse control */}
              <div className="sm:hidden absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                <button
                  type="button"
                  aria-label="Hide chat"
                  onClick={() => setCollapsed(true)}
                  className="flex items-center gap-1 cursor-pointer rounded-full bg-white/95 px-3 py-1.5 text-[12px] font-medium text-foreground/70 shadow-sm ring-1 ring-black/10 hover:text-foreground"
                >
                  <span>Hide chat</span>
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
              {/* Measurement wrapper for default height */}
              <div ref={chatContentMeasureRef} className="h-full">
                <ScriptRefineChat
                  podcast={podcast ?? undefined}
                  onMinHeightChange={(h) => setMeasuredMin(h)}
                  onSubmitSuccess={() => setCollapsed(true)}
                />
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Dialog for mobile settings */}
      <Dialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        title="Podcast settings"
        description={hasAnyGeneratedAudio ? 'Editing voices or language will remove existing generated audio.' : undefined}
        size="md"
        heightMode="auto"
        className="lg:hidden"
      >
        <div id="podcast-settings-dialog" className="space-y-6">
          <PodcastSettingsPanelContent />
          <ExportSection />
        </div>
      </Dialog>
    </div>
  )
}

export const PodcastEditorPage: React.FC = () => {

  return (
    <PodcastEditorProvider>
      <PodcastEditorPageInner />
    </PodcastEditorProvider>
  )
}

export default PodcastEditorPage
